import { useEffect, useState } from 'react';

// The back camera, at up to 1080p. `ideal`, never `exact`: a laptop or a
// phone without a back camera still gets a picture instead of an error.
export const CAMERA_CONSTRAINTS = {
  video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
  audio: false,
};

// One camera by name (components/camera/CameraPicker.js): `exact`, because a
// different camera's picture must not pass for the one chosen.
export function cameraConstraints(deviceId) {
  if (!deviceId) return CAMERA_CONSTRAINTS;
  return { video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false };
}

const isMissing = error => error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError';

const reasonOf = (error) => {
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') return 'denied';
  if (isMissing(error)) return 'notFound';
  if (error?.name === 'NotReadableError') return 'inUse';
  return 'failed';
};

// A camera that keeps ending at once is not restarted for ever.
const RESTART_LIMIT = 3;
const STEADY_MS = 5000;

/**
 * The live camera in `videoRef`, while `active`: the back camera, or the one
 * named by `deviceId`.
 *
 * Every track is stopped when the screen goes, and while the page is hidden:
 * a camera left running in a pocket drains the battery and keeps the
 * camera light on. It starts again when the page is visible.
 *
 * A camera that goes away while it runs (a USB camera unplugged) starts again.
 * If the one named by `deviceId` is not connected, the usual camera is shown
 * instead and `fellBack` says so, so the page can tell the person; it comes
 * back by itself once that camera is plugged in, as a first camera does. Nothing
 * else tells the page that a camera has been let go or a permission given, so a
 * new `attempt` asks again.
 *
 * Answers {state, reason, deviceId, label, fellBack}: state is 'starting',
 * 'live', 'paused' (hidden) or 'unavailable', with the reason ('denied',
 * 'notFound', 'inUse', 'insecure' -- plain http, where browsers offer no camera
 * at all -- 'unsupported' or 'failed'); deviceId and label are the running camera's.
 */
export function useCameraStream(videoRef, active = true, { deviceId = null, attempt = 0 } = {}) {
  const [camera, setCamera] = useState({ state: 'starting', reason: null });

  useEffect(() => {
    if (!active) return undefined;
    let stream = null;
    let generation = 0;
    let closed = false;
    let retryOnChange = false;
    let restarts = 0;
    let startedAt = 0;

    const stop = () => {
      generation += 1;   // a request still on its way is stale now
      if (stream) stream.getTracks().forEach(track => track.stop());
      stream = null;
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };

    const restart = () => {
      stop();
      // Not live for a moment, so the page reads the new camera's abilities afresh.
      setCamera({ state: 'starting', reason: null });
      start();
    };

    const start = async () => {
      const devices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
      if (!devices || typeof devices.getUserMedia !== 'function') {
        const insecure = typeof window !== 'undefined' && window.isSecureContext === false;
        setCamera({ state: 'unavailable', reason: insecure ? 'insecure' : 'unsupported' });
        return;
      }
      generation += 1;
      const mine = generation;
      retryOnChange = false;
      setCamera(c => (c.state === 'live' ? c : { state: 'starting', reason: null }));
      try {
        let granted;
        let fellBack = false;
        try {
          granted = await devices.getUserMedia(cameraConstraints(deviceId));
        } catch (error) {
          if (!deviceId || !isMissing(error)) throw error;
          if (closed || mine !== generation) return;
          fellBack = true;
          granted = await devices.getUserMedia(CAMERA_CONSTRAINTS);
        }
        // Answered after the screen went, the page was hidden, or a newer
        // request: nothing may keep running for it.
        if (closed || mine !== generation || document.visibilityState === 'hidden') {
          granted.getTracks().forEach(track => track.stop());
          return;
        }
        stream = granted;
        const video = videoRef.current;
        if (video) {
          video.srcObject = granted;
          // autoPlay covers a browser that refuses play() before a gesture.
          Promise.resolve(video.play?.()).catch(() => {});
        }
        const track = granted.getVideoTracks?.()?.[0] || null;
        track?.addEventListener?.('ended', () => {
          if (closed || mine !== generation) return;
          if (document.visibilityState === 'hidden') {
            stop();
            return;
          }
          if (Date.now() - startedAt > STEADY_MS) restarts = 0;
          restarts += 1;
          if (restarts > RESTART_LIMIT) {
            stop();
            setCamera({ state: 'unavailable', reason: 'failed' });
            return;
          }
          restart();
        });
        startedAt = Date.now();
        retryOnChange = fellBack;
        setCamera({ state: 'live', reason: null, deviceId: track?.getSettings?.()?.deviceId || null,
                    label: track?.label || '', fellBack });
      } catch (error) {
        if (closed || mine !== generation) return;
        const reason = reasonOf(error);
        retryOnChange = reason === 'notFound';
        setCamera({ state: 'unavailable', reason });
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        setCamera(c => (c.state === 'unavailable' ? c : { state: 'paused', reason: null }));
      } else if (!stream) {
        start();
      }
    };

    const onDeviceChange = () => {
      if (retryOnChange && document.visibilityState !== 'hidden') restart();
    };

    // Not what the last camera was doing: another deviceId starts from the top.
    setCamera({ state: 'starting', reason: null });
    start();
    document.addEventListener('visibilitychange', onVisibility);
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      stop();
    };
  }, [active, videoRef, deviceId, attempt]);

  return camera;
}
